/**
 * Subscription Service (Thaklis Managers) — real backend calls.
 * TC-facing: /api/subscriptions/*. Responses are snake_case, unwrapped from
 * the ApiResponse envelope ({ success, data, count }).
 */
import ApiService from './api.service';

const unwrap = (res) => (res && res.data ? res.data.data : null);

const SubscriptionService = {
  // ── TC APIs ──
  getCurrent: async () => unwrap(await ApiService.get('/api/subscriptions/current')),
  getUsage: async () => unwrap(await ApiService.get('/api/subscriptions/usage')) || [],
  getPlans: async () => unwrap(await ApiService.get('/api/subscriptions/plans')) || [],
  getPayments: async () => unwrap(await ApiService.get('/api/subscriptions/payments')) || [],
  purchase: async (data) => unwrap(await ApiService.post('/api/subscriptions/purchase', data)),
  // BE-22: what pays the plan — the TC's operations bank accounts (their debit cards) and its credit cards
  // cardsDenied: the credit card list needs `finance` access (403) — the dialog says so instead of hiding the cards
  getPaySources: async () => {
    let cardsDenied = false;
    const [banks, cards] = await Promise.all([
      ApiService.getTcBankAccounts().then(unwrap).catch(() => []),
      ApiService.get('/api/tc-credit-cards').then(unwrap).catch((e) => {
        if (e?.response?.status === 403) cardsDenied = true;
        return [];
      }),
    ]);
    return {
      banks: (banks || []).filter((b) => b.account_type === 'OPERATIONS' && (b.is_active ?? b.active) !== false),
      cards: (cards || []).filter((c) => c.active !== false),
      cardsDenied,
    };
  },
  cancel: async (reason) => unwrap(await ApiService.post('/api/subscriptions/cancel', { reason })),
  enableAutoRenew: async () => unwrap(await ApiService.post('/api/subscriptions/auto-renew/enable', {})),
  cancelAutoRenew: async () => unwrap(await ApiService.post('/api/subscriptions/auto-renew/cancel', {})),

  // ── Utilities ──
  formatCurrency: (amount, currency = 'AED') => {
    if (amount == null || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
  },

  getStatusColor: (status) => {
    const colors = {
      FREE: 'default',
      PENDING_PAYMENT: 'warning',
      ACTIVE: 'success',
      EXPIRED: 'default',
      CANCELLED: 'error',
      PAID: 'success',
      PAYMENT_FAILED: 'error',
      FREE_GRANT: 'info',
    };
    return colors[status] || 'default';
  },

  getStatusLabel: (status) => (status ? status.replace(/_/g, ' ') : ''),

  getUsagePercentage: (used, limit) => {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  },

  getUsageColor: (pct) => (pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'),

  getFeatureLabel: (code, isB2b = false) => {
    const labels = {
      ORG_COUNT: isB2b ? 'Typing Centers' : 'Clients',
      TC_USER_COUNT: 'Users',
      INVOICE_MONTHLY: 'Invoices / month',
      WORK_ORDER_MONTHLY: 'Work Orders / month',
      QUOTATION_MONTHLY: 'Quotations / month',
      MAX_B2B_CONNECTIONS: 'Providers',
      MAX_B2B_CLIENTS: 'Client Typing Centers',
    };
    return labels[code] || code;
  },

  // Cycle label from a price row (snake_case from backend).
  cycleLabel: (price) => {
    const count = price.period_count;
    const unit = (price.period_unit || '').toLowerCase();
    return count === 1 ? `1 ${unit}` : `${count} ${unit}s`;
  },
};

export default SubscriptionService;
